import React, { useEffect, useRef, useState } from 'react'
import { Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Header2 from '../components/Header2'
import Footer from '../components/Footer'
import SideBar from '../components/SideBar'
import SideResponsiveOpen from '../components/SideResponsiveOpen'
import NoDashboardCard from "../components/NoDashboardsCard"
import DashboardListItem from "../components/DashboardListItem"
import SharedDashboardListItem from "../components/SharedDashboardListItem"
import DeleteDashboardModal from '../components/DeleteDashboardModal'
import Message from "../components/Message"
import {
  getDashboardList
} from '../actions/platformActions'
import { exportDashboard, importDashboard } from '../services/dashboardImportExportApi'
import { getSharedWithMe, getSharedByMe } from '../services/dashboardSharingApi'
import { slugifyFilename } from '../utils/exportDashboardPdf'
import '../styles/dashboardActions.css'
import '../styles/dashboardSharedTabs.css'
import {
  CREATE_DASHBOARD_RESET,
  DELETE_DASHBOARD_RESET,
} from '../constants/platformConstants'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024 // 5MB — matches the widget/count caps enforced server-side


function DashboardScreen() {

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [deleteDashboardModal, setDeleteDashboardModal] = useState(false)
  const [selectedDashboard, setSelectedDashboard] = useState(null)
  const [dashboardList, setDashboardList] = useState([])

  const userLogin = useSelector(state => state.userLogin)
  const { userInfo } = userLogin

  const importInputRef = useRef(null)
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState(null)

  const [exportingId, setExportingId] = useState(null)
  const [exportError, setExportError] = useState(null)

  const [activeTab, setActiveTab] = useState('mine')
  const [sharedFilter, setSharedFilter] = useState('all')
  const [sharedWithMe, setSharedWithMe] = useState([])
  const [sharedByMe, setSharedByMe] = useState([])
  const [sharedLoading, setSharedLoading] = useState(false)
  const [sharedError, setSharedError] = useState(null)

  useEffect(() => {
    // if (typeof window.trackPageVisit === 'function') {
    //   window.trackPageVisit('Dashboards List');
    // }

    dispatch({
      type: CREATE_DASHBOARD_RESET
    })
    dispatch(getDashboardList())
  }, [])

  const dashboardInfo = useSelector(state => state.dashboardList)
  let { loading, error, dashboards } = dashboardInfo

  const isEmpty = (dashboardList.length == 0)

  useEffect(() => {
    if (dashboards) {
      setDashboardList(dashboards.data)
    }
  }, [dashboardInfo])

  // Fetched once on mount, never polled — both endpoints are throttled
  // server-side, and this is only what decides whether the Shared tab
  // renders at all plus what fills it.
  useEffect(() => {
    if (!userInfo?.access) return
    setSharedLoading(true)
    setSharedError(null)
    Promise.all([
      getSharedWithMe(userInfo.access),
      getSharedByMe(userInfo.access),
    ])
      .then(([withMe, byMe]) => {
        setSharedWithMe(withMe.data || [])
        setSharedByMe(byMe.data || [])
      })
      .catch(() => setSharedError('Unable to load shared dashboards.'))
      .finally(() => setSharedLoading(false))
  }, [userInfo?.access])

  const hasSharedDashboards = sharedWithMe.length > 0 || sharedByMe.length > 0

  useEffect(() => {
    // A dashboard can go from shared to not-shared (or vice versa) between
    // loads — never leave the tab strip pointed at a tab that just
    // disappeared.
    if (!hasSharedDashboards && activeTab === 'shared') {
      setActiveTab('mine')
    }
  }, [hasSharedDashboards, activeTab])

  const sharedItems = sharedFilter === 'with-me'
    ? sharedWithMe.map(item => ({ data: item, variant: 'with-me' }))
    : sharedFilter === 'by-me'
      ? sharedByMe.map(item => ({ data: item, variant: 'by-me' }))
      : [
        ...sharedWithMe.map(item => ({ data: item, variant: 'with-me' })),
        ...sharedByMe.map(item => ({ data: item, variant: 'by-me' })),
      ]

  const closeDeleteDashboardHandler = (isDeleted, did) => {
    setDeleteDashboardModal(false)
    // dashboards = dashboards.filter(item => item.id != did)
    if (isDeleted) {
      setDashboardList(dashboardList.filter(item => item.id != did))
    }
  }

  const deleteDashboardHandler = (did) => {
    dispatch({
      type: DELETE_DASHBOARD_RESET,
    })
    setSelectedDashboard(did)
    setDeleteDashboardModal(true)
  }

  const exportDashboardHandler = async (item) => {
    setExportError(null)
    setExportingId(item.id)
    try {
      const data = await exportDashboard(item.code_id, userInfo.access)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const safeName = slugifyFilename(item.dashboard_name)
      const link = document.createElement('a')
      link.href = url
      link.download = `${safeName}-export.json`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setExportError('Unable to export this dashboard. Please try again.')
    } finally {
      setExportingId(null)
    }
  }

  const importDashboardClickHandler = () => {
    setImportError(null)
    importInputRef.current?.click()
  }

  const importFileSelectedHandler = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = '' // allow re-selecting the same file later
    if (!file) return

    setImportError(null)

    if (file.size > MAX_IMPORT_FILE_BYTES) {
      setImportError('That file is too large to import.')
      return
    }

    let payload
    try {
      payload = JSON.parse(await file.text())
    } catch (err) {
      setImportError('That file is not valid JSON.')
      return
    }

    // Fast client-side sanity check only — the server independently
    // validates and sanitizes everything before writing anything to the DB.
    if (typeof payload !== 'object' || payload === null || !('schema_version' in payload)) {
      setImportError('That file does not look like a dashboard export.')
      return
    }

    setImporting(true)
    try {
      const created = await importDashboard(payload, userInfo.access)
      navigate(`/dashboard/${created.code_id}`)
    } catch (err) {
      const detail = err.response?.data
      const message = typeof detail === 'string'
        ? detail
        : (detail && Object.values(detail).flat().find(Boolean)) || 'Unable to import this dashboard.'
      setImportError(String(message))
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className='h-100 d-flex flex-column'>
      <Header2 />
      <div className='d-flex' style={{ backgroundColor: '#F4F5F7', flex: '1' }}>
        <SideBar />
        <div className='dashboard-main-container'>
          <h1 className='p-title pb-4'>Dashboards</h1>
          <NoDashboardCard isEmpty={isEmpty} />

          <hr />

          {hasSharedDashboards && (
            <div className='dashboard-tabs' role='tablist'>
              <button
                type='button'
                role='tab'
                aria-selected={activeTab === 'mine'}
                className={`dashboard-tab-button ${activeTab === 'mine' ? 'dashboard-tab-active' : ''}`}
                onClick={() => setActiveTab('mine')}
              >
                My dashboards
              </button>
              <button
                type='button'
                role='tab'
                aria-selected={activeTab === 'shared'}
                className={`dashboard-tab-button ${activeTab === 'shared' ? 'dashboard-tab-active' : ''}`}
                onClick={() => setActiveTab('shared')}
              >
                Shared
              </button>
            </div>
          )}

          {activeTab === 'mine' ? (
            <>
              <div className='d-flex justify-content-between align-items-center flex-wrap'>
                <p style={{ color: '#616161' }} className='p-paragraph mb-0'>
                  Below is the list of created dashboards. Select a dashboard to access its detailed visualizations and data analysis.
                </p>
                <div>
                  <input
                    ref={importInputRef}
                    type='file'
                    accept='application/json,.json'
                    className='d-none'
                    onChange={importFileSelectedHandler}
                  />
                  <button
                    onClick={importDashboardClickHandler}
                    disabled={importing}
                    className='btn btn-outline-dark fw-bold'
                  >
                    {importing ? (
                      <Spinner animation="border" as={'span'} style={{ width: '16px', height: '16px' }} />
                    ) : (
                      <><i className="fa-solid fa-file-import pe-2"></i>Import Dashboard</>
                    )}
                  </button>
                </div>
              </div>
              {importError && (
                <Message variant='danger'>{importError}</Message>
              )}
              {exportError && (
                <Message variant='danger'>{exportError}</Message>
              )}
              {loading && (
                <div className='d-flex justify-content-center'>
                  <Spinner animation="border" as={'span'} style={{ width: '20px', height: '20px' }} />
                </div>
              )}
              {error && (
                <Message variant='danger'>{error}</Message>
              )}
              {dashboards && dashboardList.map(item => (
                <div key={item.id} className='d-flex align-items-center'>
                  <DashboardListItem data={item} />
                  <div className='dashboard-list-actions ps-3'>
                    {item.shared_with_count > 0 && (
                      <OverlayTrigger placement="top" overlay={<Tooltip id={`shared-tooltip-${item.id}`}>{`Shared with ${item.shared_with_count} ${item.shared_with_count === 1 ? 'person' : 'people'}`}</Tooltip>}>
                        <span className='dashboard-shared-badge-icon'>
                          <i className='fa-solid fa-user-group' aria-hidden='true'></i>
                        </span>
                      </OverlayTrigger>
                    )}
                    <OverlayTrigger placement="top" overlay={<Tooltip id={`export-tooltip-${item.id}`}>Export dashboard as JSON</Tooltip>}>
                      <i
                        onClick={exportingId ? undefined : () => exportDashboardHandler(item)}
                        className={`fa-solid ${exportingId === item.id ? 'fa-spinner fa-spin' : 'fa-file-export'} dashboard-action-icon-btn dashboard-action-export ${exportingId ? 'dashboard-action-btn-disabled' : ''}`}
                      ></i>
                    </OverlayTrigger>
                    <OverlayTrigger placement="top" overlay={<Tooltip id={`delete-tooltip-${item.id}`}>Delete dashboard</Tooltip>}>
                      <i
                        onClick={e => deleteDashboardHandler(item.id)}
                        className="fa-solid fa-trash dashboard-action-icon-btn dashboard-action-delete"
                      ></i>
                    </OverlayTrigger>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <div className='dashboard-shared-filter' role='group' aria-label='Filter shared dashboards'>
                <button
                  type='button'
                  className={`dashboard-shared-filter-button ${sharedFilter === 'all' ? 'dashboard-shared-filter-active' : ''}`}
                  onClick={() => setSharedFilter('all')}
                >
                  All
                </button>
                <button
                  type='button'
                  className={`dashboard-shared-filter-button ${sharedFilter === 'with-me' ? 'dashboard-shared-filter-active' : ''}`}
                  onClick={() => setSharedFilter('with-me')}
                >
                  Shared with me
                </button>
                <button
                  type='button'
                  className={`dashboard-shared-filter-button ${sharedFilter === 'by-me' ? 'dashboard-shared-filter-active' : ''}`}
                  onClick={() => setSharedFilter('by-me')}
                >
                  Shared by me
                </button>
              </div>

              {sharedLoading && (
                <div className='d-flex justify-content-center'>
                  <Spinner animation="border" as={'span'} style={{ width: '20px', height: '20px' }} />
                </div>
              )}
              {sharedError && (
                <Message variant='danger'>{sharedError}</Message>
              )}
              {!sharedLoading && !sharedError && sharedItems.length === 0 && (
                <p className='dashboard-empty-shared-message'>No dashboards match this filter.</p>
              )}
              {sharedItems.map(({ data, variant }) => (
                <div key={`${variant}-${data.id}`} className='d-flex align-items-center'>
                  <SharedDashboardListItem data={data} variant={variant} />
                </div>
              ))}
            </>
          )}

        </div>
        {deleteDashboardModal && (
          <DeleteDashboardModal closeCallback={closeDeleteDashboardHandler} did={selectedDashboard} />
        )}
      </div>

      <SideResponsiveOpen />
      <Footer />
    </div>
  )
}

export default DashboardScreen
